import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { TourSection, getSectionStartIndex } from '../constants/tour';
import { tourSteps } from '../constants/tourSteps';

const TOUR_STORAGE_KEY = 'skylarq_tour_completed';
const TOUR_STEP_KEY = 'skylarq_tour_step';
const SAMPLE_DATASET_KEY = 'skylarq_sample_dataset_id';

interface ProductTourContextType {
  isActive: boolean;
  currentStep: number;
  isCompleted: boolean;
  sampleDatasetId: string | null;
  totalSteps: number;
  startTour: () => void;
  startTourAtSection: (section: TourSection) => void;
  endTour: () => void;
  skipTour: () => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  resetTour: () => void;
  setSampleDatasetId: (id: string) => void;
}

const ProductTourContext = createContext<ProductTourContextType | undefined>(undefined);

export const useProductTour = () => {
  const context = useContext(ProductTourContext);
  if (!context) {
    throw new Error('useProductTour must be used within a ProductTourProvider');
  }
  return context;
};

export const ProductTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(() => localStorage.getItem(TOUR_STORAGE_KEY) === 'true');
  const [sampleDatasetId, setSampleDatasetIdState] = useState<string | null>(() => localStorage.getItem(SAMPLE_DATASET_KEY));

  useEffect(() => {
    const savedStep = localStorage.getItem(TOUR_STEP_KEY);
    if (savedStep) {
      const parsedStep = parseInt(savedStep, 10);
      if (!isNaN(parsedStep) && parsedStep >= 0 && parsedStep < tourSteps.length) {
        setCurrentStep(parsedStep);
      }
    }
  }, []);

  useEffect(() => {
    if (isActive) localStorage.setItem(TOUR_STEP_KEY, currentStep.toString());
  }, [currentStep, isActive]);

  const startTour = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    localStorage.removeItem(TOUR_STEP_KEY);
  }, []);

  const startTourAtSection = useCallback((section: TourSection) => {
    const startIndex = getSectionStartIndex(section);
    setIsActive(true);
    setCurrentStep(startIndex >= 0 ? startIndex : 0);
    localStorage.setItem(TOUR_STEP_KEY, (startIndex >= 0 ? startIndex : 0).toString());
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    setIsCompleted(true);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
  }, []);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setIsCompleted(true);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.removeItem(TOUR_STEP_KEY);
  }, []);

  const setStep = useCallback((step: number) => setCurrentStep(step), []);
  const nextStep = useCallback(() => setCurrentStep(prev => Math.min(prev + 1, tourSteps.length - 1)), []);
  const prevStep = useCallback(() => setCurrentStep(prev => Math.max(prev - 1, 0)), []);

  const resetTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setIsCompleted(false);
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem(TOUR_STEP_KEY);
  }, []);

  const setSampleDatasetId = useCallback((id: string) => {
    setSampleDatasetIdState(id);
    localStorage.setItem(SAMPLE_DATASET_KEY, id);
  }, []);

  return (
    <ProductTourContext.Provider value={{
      isActive, currentStep, isCompleted, sampleDatasetId, totalSteps: tourSteps.length,
      startTour, startTourAtSection, endTour, skipTour, setStep, nextStep, prevStep, resetTour, setSampleDatasetId,
    }}>
      {children}
    </ProductTourContext.Provider>
  );
};
